import { z } from 'zod';
import { GSTIN_PATTERN, PAN_PATTERN, resolveState } from '../tax.js';

/**
 * Who sells what. The store itself (its details are in Admin > GST & tax, see taxSettingsSchema) sells every product that
 * has no seller; other businesses selling on the platform are "sellers". A seller's GSTIN, PAN and state are checked the
 * same way as the store's.
 */

/** Checks a GSTIN and a PAN against each other and against the state; adds the problems to `ctx` on the right fields. */
export function checkBusinessIds(v: { gstin: string; pan: string; state: string }, ctx: z.RefinementCtx, stateField: string): void {
  const state = resolveState(v.state);
  if (!state) ctx.addIssue({ code: 'custom', path: [stateField], message: 'Pick one of the listed states' });
  if (v.pan && !PAN_PATTERN.test(v.pan)) ctx.addIssue({ code: 'custom', path: ['pan'], message: 'A PAN is 10 characters, like ABCDE1234F' });
  if (!v.gstin) return;
  if (!GSTIN_PATTERN.test(v.gstin)) {
    ctx.addIssue({ code: 'custom', path: ['gstin'], message: 'A GSTIN is 15 characters, like 09ABCDE1234F1Z5' });
    return;
  }
  if (state && v.gstin.slice(0, 2) !== state.code) ctx.addIssue({ code: 'custom', path: ['gstin'], message: `A GSTIN for ${state.name} starts with ${state.code}` });
  // characters 3-12 of a GSTIN are the holder's PAN
  else if (v.pan && PAN_PATTERN.test(v.pan) && v.gstin.slice(2, 12) !== v.pan) ctx.addIssue({ code: 'custom', path: ['gstin'], message: 'This GSTIN belongs to a different PAN: its characters 3 to 12 should read ' + v.pan });
}

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .refine((v) => v === '' || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v), 'Enter a valid email address')
  .default('');

export const sellerInputSchema = z
  .object({
    /** the name shoppers see */
    name: z.string().trim().min(1, 'Give the seller a name').max(80),
    /** the registered name, when it differs */
    legalName: z.string().trim().max(120).default(''),
    /** "Sole proprietorship", "LLP", "Private limited"... */
    businessType: z.string().trim().max(60).default(''),
    gstin: z.string().trim().toUpperCase().default(''),
    pan: z.string().trim().toUpperCase().default(''),
    state: z.string().trim().min(1, 'Pick the state the seller is registered in'),
    address: z.string().trim().max(400).default(''),
    contactEmail: optionalEmail,
    contactPhone: z.string().trim().max(30).default(''),
    isActive: z.boolean().default(true),
  })
  .superRefine((v, ctx) => checkBusinessIds(v, ctx, 'state'))
  .transform((v) => ({ ...v, state: resolveState(v.state)?.name ?? v.state }));
export type SellerInput = z.infer<typeof sellerInputSchema>;

/** A seller as the admin lists it. */
export interface AdminSellerDTO extends SellerInput {
  id: string;
  productCount: number;
  createdAt: string;
}

/**
 * A business as an invoice, a label or a product page shows it. It is also what gets written on an order, so a seller
 * changing its details later does not rewrite what was sold.
 */
export interface SellerProfile {
  /** the id of the seller, or null for the store itself */
  id: string | null;
  /** the name shoppers know */
  name: string;
  /** the name it is registered under (the name again when none is set) */
  legalName: string;
  businessType: string;
  gstin: string;
  pan: string;
  state: string;
  stateCode: string;
  addressLines: string[];
}

/** What a shopper sees on a product page. */
export interface ProductSellerDTO extends Pick<SellerProfile, 'name' | 'legalName' | 'businessType' | 'gstin' | 'state'> {
  /** true when the store itself sells it */
  isStore: boolean;
  /** "MeshSmith is a product of ScrawlByte Solution": set for the store when it trades under another name, else empty */
  brandLine: string;
}

/**
 * "MeshSmith is a product of ScrawlByte Solution": what documents and product pages say while the platform trades under
 * another business's registration. Empty when the registered name already carries the store's name ("MeshSmith", "MeshSmith
 * Creative LLP"), so it stops by itself once MeshSmith has its own registration.
 */
export function brandLine(storeName: string, legalName: string): string {
  const tidy = (t: string) => t.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!legalName.trim() || !storeName.trim() || tidy(legalName).startsWith(tidy(storeName))) return '';
  return `${storeName.trim()} is a product of ${legalName.trim()}`;
}

/** The sellers page: the store itself (edited in GST & tax), then the sellers added here. */
export interface AdminSellersDTO {
  store: ProductSellerDTO;
  sellers: AdminSellerDTO[];
}

/** For the seller choice in the product editor. */
export interface SellerOptionsDTO {
  store: ProductSellerDTO;
  sellers: { id: string; name: string; isActive: boolean }[];
}
